import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle2, ShoppingBag } from 'lucide-react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

interface HeroProps {
  onLearnMore: () => void;
  onShopProduct: (productId: string) => void;
}

const HERO_SLIDES = [
  {
    image: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&q=80&w=2000',
    title: 'Ancient Wisdom',
    accent: 'Healing Nature,',
    description: 'Discover the power of 100% natural Indonesian herbal remedies. Certified GMP, ISO & HALAL for your peace of mind.',
    cta: { text: 'Shop Remedies', type: 'general' }
  },
  {
    image: 'https://images.unsplash.com/photo-1552674605-5d226a5d6106?auto=format&fit=crop&q=80&w=2000',
    title: 'Prostalon',
    accent: 'Prostate Health with',
    description: 'Traditionally used to help ease urination and support prostate health with natural herbal ingredients.',
    cta: { text: 'Shop Prostalon', type: 'product', productId: '31' }
  },
  {
    image: '',
    title: 'Wasir',
    accent: 'Natural Relief with',
    description: 'Herbal remedy formulated to help relieve symptoms associated with hemorrhoids and reduce swelling.',
    cta: { text: 'Shop Wasir', type: 'product', productId: '32' }
  }
];

export const Hero: React.FC<HeroProps> = ({ onLearnMore, onShopProduct }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const container = useRef<HTMLElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 8000);

    return () => clearInterval(timer);
  }, []);

  useGSAP(() => {
    const tl = gsap.timeline();
    tl.from('.hero-badge', { opacity: 0, x: -20, duration: 0.6, ease: 'power2.out' })
      .from('.hero-title', { opacity: 0, y: 30, duration: 0.8, ease: 'power3.out' }, '-=0.3')
      .from('.hero-desc', { opacity: 0, y: 20, duration: 0.6, ease: 'power2.out' }, '-=0.4')
      .from('.hero-buttons', { opacity: 0, y: 20, duration: 0.6, ease: 'power2.out' }, '-=0.4')
      .from('.hero-trust', { opacity: 0, duration: 0.8 }, '-=0.2');
  }, { scope: container });

  const activeSlide = HERO_SLIDES[currentSlide];

  return (
    <section ref={container} id="home" className="relative bg-herbal-900 overflow-hidden min-h-[650px] lg:min-h-[750px] flex items-center">
      {/* Background Image Slides */}
      <div className="absolute inset-0 z-0">
        {HERO_SLIDES.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 scale-105' : 'opacity-0 scale-100'
            } transform transition-transform duration-[10000ms]`}
          >
            {slide.image && (
              <img 
                src={slide.image} 
                alt={slide.title} 
                className="w-full h-full object-cover opacity-30"
                loading={index === 0 ? "eager" : "lazy"}
              />
            )}
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-herbal-900 via-herbal-900/80 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-herbal-900 via-transparent to-transparent opacity-60"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="lg:w-2/3">
          <div className="hero-badge flex items-center gap-2 mb-6">
             <span className="bg-turmeric-500 text-herbal-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
               #1 in East Africa
             </span>
             <span className="text-herbal-100 text-sm font-medium tracking-wide">Premium Herbal Care</span>
          </div>
          
          <div className="hero-title h-[180px] sm:h-[220px] lg:h-[280px]">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 transition-all duration-700">
              <span className="block opacity-90">{activeSlide.accent}</span>
              <span className="text-turmeric-500">{activeSlide.title}.</span>
            </h1>
          </div>
          
          <p className="hero-desc font-sans text-lg sm:text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
            {activeSlide.description}
          </p>

          <div className="hero-buttons flex flex-col sm:flex-row gap-4 mb-12">
            {activeSlide.cta.type === 'product' ? (
              <button 
                onClick={() => onShopProduct(activeSlide.cta.productId!)}
                className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-full text-herbal-900 bg-turmeric-500 hover:bg-turmeric-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <ShoppingBag className="mr-2 h-5 w-5" />
                {activeSlide.cta.text}
              </button>
            ) : (
              <a href="#products" className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-full text-herbal-900 bg-turmeric-500 hover:bg-turmeric-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                Shop Remedies
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            )}
            
            <button 
              onClick={onLearnMore}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-base font-semibold rounded-full text-white hover:bg-white/10 transition-all backdrop-blur-sm"
            >
              Learn About Jamu
            </button>
          </div>

          <div className="hero-trust flex flex-wrap gap-6 text-sm text-gray-300 font-medium pt-8 border-t border-white/10">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-turmeric-500" />
              <span>GMP Certified</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-turmeric-500" />
              <span>ISO Standard</span>
            </div>
            <div className="flex items-center gap-2 lg:hidden xl:flex">
              <CheckCircle2 className="h-5 w-5 text-turmeric-500" />
              <span>HALAL Approved</span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
          {HERO_SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 transition-all duration-300 rounded-full ${
                index === currentSlide ? 'w-8 bg-turmeric-500' : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
